import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SignUpFormContext } from "../sign-up-form.context";
import { TextFieldComponent, IconButtonComponent, TypographyComponent, CheckmarkIcon, EyeIcon, EyeSlashIcon } from "@gx1/gx-ui-common";
import classNames from "classnames";
import styles from "./validate-password.module.scss";

const ValidatePassword = () => {
  const { t } = useTranslation();
  const {
    password,
    setPassword,
    showPassword,
    setShowPassword,
    setIsValidPwd,
    isBreachedPassword,
    confirmPassword,
    setConfirmPassword,
  } = useContext(SignUpFormContext);

  const [isBetween8And40, setIsBetween8And40] = useState(false);
  const [hasOneLetter, setHasOneLetter] = useState(false);
  const [hasOneSpChar, setHasOneSpChar] = useState(false);
  const [isPwdOrGartner, setIsPwdOrGartner] = useState(false);
  const [_passConfirmed, setPassConfirmed] = useState(false);

  useEffect(() => {
    setIsBetween8And40(password.length >= 8 && password.length <= 40);
    setHasOneLetter(containsAnyLetter(password));
    setHasOneSpChar(containsAnySpecCharOrNum(password));
    setIsPwdOrGartner(containsGartnerOrPassword(password));
    setPassConfirmed(password === confirmPassword && password !== "");

    setIsValidPwd(
      password.length >= 8 &&
        password.length <= 40 &&
        containsAnyLetter(password) &&
        containsAnySpecCharOrNum(password) &&
        password === confirmPassword &&
        !containsGartnerOrPassword(password)
    );
  }, [password, setIsValidPwd, confirmPassword]);

  const containsAnyLetter = (str: string) => {
    return /[a-zA-Z]/.test(str);
  };

  const containsAnySpecCharOrNum = (str: string) => {
    const specialChars = /[`\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
    return specialChars.test(str);
  };

  const containsGartnerOrPassword = (str: string) => {
    const gartnerOrPassword = /gartner|password/i;
    return gartnerOrPassword.test(str);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={styles.root}>
      <TextFieldComponent
        inputProps={{ autoComplete: "current-password" }}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t("Password")}
        rootClassName={"gx-dragon-password-input"}
        InputProps={{ type: showPassword ? "text" : "password" }}
        value={password}
        isError={isBreachedPassword}
        endIcon={
          <IconButtonComponent
            ariaLabel={showPassword ? t("Hide password") : t("Show password")}
            onClick={handleClickShowPassword}
            size={"small"}
          >
            {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
          </IconButtonComponent>
        }
      />
      <div className={styles.checkedContainer}>
        <div className={classNames(styles.checkRow, !isPwdOrGartner && styles.iconGreen)}>
          <CheckmarkIcon />
          <TypographyComponent styling="p-xs" color="grey9">
            {t("Cannot contain ‘password’ or ‘gartner’")}
          </TypographyComponent>
        </div>
        <div className={classNames(styles.checkRow, isBetween8And40 && styles.iconGreen)}>
          <CheckmarkIcon />
          <TypographyComponent styling="p-xs" color="grey9">
            {t("Between 8 and 40 characters")}
          </TypographyComponent>
        </div>
        <div className={classNames(styles.checkRow, hasOneLetter && styles.iconGreen)}>
          <CheckmarkIcon />
          <TypographyComponent styling="p-xs" color="grey9">
            {t("At least one letter")}
          </TypographyComponent>
        </div>
        <div className={classNames(styles.checkRow, hasOneSpChar && styles.iconGreen)}>
          <CheckmarkIcon />
          <TypographyComponent styling="p-xs" color="grey9">
            {t("At least one number or special character")}
          </TypographyComponent>
        </div>
      </div>
      <TextFieldComponent
        inputProps={{ autoComplete: "current-password" }}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder={t("Confirm Password")}
        InputProps={{ type: showPassword ? "text" : "password" }}
        value={confirmPassword || ""}
        isError={isBreachedPassword}
        endIcon={
          <IconButtonComponent
            ariaLabel={showPassword ? t("Hide confirm password") : t("Show confirm password")}
            onClick={handleClickShowPassword}
            size={"small"}
          >
            {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
          </IconButtonComponent>
        }
      />
    </div>
  );
};

export default ValidatePassword;
